// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import { useEffect, useState } from "react";
import { useKeycloak } from "../context/KeycloakProvider";
import { tenantSettingsService } from "../services/tenantSettingsService";
import { hasSuperAdminAccess } from "../config/roleConfig";
import { TENANT_SETTINGS_UPDATED_EVENT } from "../utils/tenantSettingsEvents";

/**
 * Hook to get current tenant's branding (name, logo, colors).
 * Uses the tenant settings endpoint, which is already tenant-aware on the backend.
 */
const useTenantBranding = () => {
  const { getUserInfo } = useKeycloak();
  const userInfo = getUserInfo();

  const allRoles = [
    ...(userInfo?.roles || []),
    ...(userInfo?.resourceRoles
      ? Object.values(userInfo.resourceRoles).flatMap((r) => r.roles || [])
      : []),
  ];

  const isSuperAdmin = hasSuperAdminAccess(allRoles);
  const tenantId = isSuperAdmin
    ? userInfo?.tenant_id
    : (userInfo?.tenant_id ?? null);

  const [branding, setBranding] = useState({});
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    let isDisposed = false;

    const loadBranding = async () => {
      if (!tenantId) {
        if (!isDisposed) {
          setBranding({});
          setError(null);
          setLoading(false);
        }
        return;
      }

      setLoading(true);
      try {
        const data = await tenantSettingsService.getTenantSettings(tenantId);

        let nextBranding = {};

        if (data) {
          if (data.branding && typeof data.branding === "object") {
            nextBranding = data.branding;
          } else if (
            data.settings &&
            typeof data.settings === "object" &&
            !Array.isArray(data.settings) &&
            data.settings.branding &&
            typeof data.settings.branding === "object"
          ) {
            nextBranding = data.settings.branding;
          }
          if (
            !Object.keys(nextBranding).length &&
            Array.isArray(data.settings)
          ) {
            const parsed = tenantSettingsService.parseSettingsByCategory(
              data.settings,
            );
            nextBranding = parsed.branding || {};
          }
        }

        if (!isMounted || isDisposed) return;
        setBranding(nextBranding || {});
        setError(null);

        // Logo comes from the picture API (keyed by tenant_id), fetched as an
        // authenticated blob since the endpoint isn't public.
        const url = await tenantSettingsService.getTenantLogoUrl(tenantId);
        if (!isMounted || isDisposed) {
          if (url) URL.revokeObjectURL(url);
          return;
        }
        setLogoUrl(url);
      } catch (err) {
        console.error("Failed to load tenant branding settings:", err);
        if (!isMounted || isDisposed) return;
        setBranding({});
        setError(err);
      } finally {
        if (isMounted && !isDisposed) {
          setLoading(false);
        }
      }
    };

    loadBranding();

    const handleTenantSettingsUpdated = (event) => {
      const updatedTenantId = event?.detail?.tenantId ?? null;
      if (!tenantId) return;
      if (updatedTenantId && updatedTenantId !== tenantId) return;
      loadBranding();
    };
    window.addEventListener(
      TENANT_SETTINGS_UPDATED_EVENT,
      handleTenantSettingsUpdated,
    );

    return () => {
      isMounted = false;
      isDisposed = true;
      window.removeEventListener(
        TENANT_SETTINGS_UPDATED_EVENT,
        handleTenantSettingsUpdated,
      );
    };
  }, [tenantId]);

  // Revoke the previous blob URL whenever it changes or the hook unmounts.
  useEffect(() => {
    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    };
  }, [logoUrl]);

  const companyName = branding.company_name || null;
  const primaryColor = branding.primary_color || "#3B82F6";
  const secondaryColor = branding.secondary_color || "#1E40AF";
  return {
    branding,
    loading,
    error,
    companyName,
    logoUrl,
    primaryColor,
    secondaryColor,
  };
};

export default useTenantBranding;
